import React from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormInput } from "go-form";
import { mapToBase } from "go-core/api/searchSelectService";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { adminApi } from "../../../../../../../services/Api/Admin/adminApi";
import { PartnerApi, PartnerUserApi } from "../../../../../../../services/Api/Partner/partnerTypes";
import { api } from "../../../../../../../services/Security/api";

interface Props {
	form: UseFormReturn<PartnerApi>;
	partner?: PartnerApi;
}

const PartnerBasicInfoForm = ({ form, partner }: Props) => {
	const { t } = useTranslation("admin");
	const {
		register,
		formState: { errors },
		control,
		getValues,
		setValue,
		watch,
	} = form;

	const onCreateCode = (val: string) => {
		setValue("codes", [...getValues().codes, val], { shouldDirty: true });
	};

	const onCreateTag = (val: string) => {
		setValue("tags", [...getValues().tags, val], { shouldDirty: true });
	};

	const mapTags = (tags: string[]) => {
		return tags?.map((tag) => {
			return {
				value: tag,
				label: tag,
			};
		});
	};

	const searchUsers = async (search: string, params: Record<string, any>, options?: Record<string, any>) => {
		const res = await api.user().getUsersSearchSelect(search, params, {
			cancelToken: options?.token,
		});

		return res.data.data.map((user: { label: string; id: string; email: string }) => ({
			name: user.label,
			user_id: Number(user.id),
			email: user.email,
		}));
	};

	const getUserTags = (users?: PartnerUserApi[]) => {
		return users?.map((user) => {
			return {
				name: user.user_email,
				user_id: user.user_id,
			};
		});
	};

	const onChangeTags = (values: string[]) => {
		setValue("tags", values);
	};

	const renderUser = (user: { id: string; name: string; email: string }, labelMeta: any) => {
		return (
			<div className={"d-flex"}>
				{user.name}{" "}
				{user.email ? (
					<small style={{ fontSize: labelMeta.context === "value" ? "0.7rem" : "0.85rem" }}>
						&nbsp;({user.email})
					</small>
				) : null}
			</div>
		);
	};

	const searchPartners = (search: string, params: Record<string, any>) => {
		const newParams = {
			...params,
			include: "default_provisions",
			"id|ne": partner?.id || -1,
		};

		return adminApi.getAdminPartnersSearchSelect(search, newParams);
	};

	const searchTags = async (search: string) => {
		const filter = mapToBase(search, { resource_type: "PARTNER" });
		const res = await adminApi.getTagsListSearchSelect(filter);
		return res.map((value) => ({
			label: value.label,
			value: value.label,
		}));
	};

	const partnerTypes = ["RESELLER", "KAM", "TOP", "SALE", "AFFILIATE", "CS", "CSV", "SALE_PREMIUM"].map((type) => ({
		value: type,
		label: type,
	}));

	const handleChangeUsers = (users: { user_id: string; email: string; label: string }[]) => {
		setValue(
			"users",
			users.map((user) => ({
				user_id: Number(user.user_id),
			})) as PartnerUserApi[]
		);
	};
	return (
		<fieldset className={"form-group no-gutters"}>
			<h5>{t("common.word.basic_data", { ns: "lib" })}</h5>
			<div className={"row"}>
				<div className={"col-md-12"}>
					<FormInput label={t("lib:common.word.name")} register={register} name="name" errors={errors} />
				</div>
				<div className={"col-md-12"}>
					<FormInput
						label={t("modules.partner.field.keeper.title")}
						register={register}
						name="keeper"
						errors={errors}
					/>
				</div>
				{!partner?.id && (
					<div className={"col-md-12"}>
						<FormInput
							help={t("modules.partner.field.slug.help_text")}
							label={t("modules.partner.field.slug.title")}
							register={register}
							name="slug"
							errors={errors}
						/>
					</div>
				)}
				<div className={"col-md-12"}>
					<FormSelectGroup
						control={control}
						options={partnerTypes}
						isClearable={false}
						defaultValue={partner?.type}
						errors={errors}
						name="type"
						label={t("common.word.partner_type")}
						data-testid="type"
					/>
				</div>
				<div className={"col-md-12"}>
					<FormSelectGroup
						control={control}
						onCreateOption={onCreateCode}
						isMulti
						loadOptions={() => mapTags(partner?.codes || [])}
						defaultValue={mapTags(watch("codes"))}
						errors={errors}
						name="codes"
						label={t("modules.partner.field.codes.title")}
						data-testid="codes"
					/>
				</div>
				<div className={"col-md-12"}>
					<FormSelectGroup
						control={control}
						onCreateOption={onCreateTag}
						isMulti
						loadOptions={searchTags}
						onChange={onChangeTags}
						defaultValue={mapTags(watch("tags"))}
						errors={errors}
						name="tags"
						label={t("common.word.tags")}
						data-testid="tags"
					/>
				</div>
				<div className={"col-md-12"}>
					<FormSelectGroup
						control={control}
						isMulti
						errors={errors}
						name="users"
						formatOptionLabel={(opt, labelMeta) => renderUser(opt, labelMeta)}
						defaultValue={getUserTags(partner?.users)}
						getOptionLabel={(option) => option.name}
						getOptionValue={(option) => Number(option.user_id)}
						loadOptions={searchUsers}
						label={t("lib:common.word.users")}
						onChange={(_, obj) => handleChangeUsers(obj)}
						data-testid="users"
					/>
				</div>
				<div className={"col-md-12"}>
					<FormSelectGroup
						control={control}
						errors={errors}
						name="parent_id"
						defaultValue={partner?.parent}
						isClearable
						getOptionLabel={(option) => option.name}
						getOptionValue={(option) => option.id}
						loadOptions={searchPartners}
						label={t("common.word.partner")}
						data-testid="parent_id"
					/>
				</div>
			</div>
		</fieldset>
	);
};

export default PartnerBasicInfoForm;
